import { fetchCustomerContacts } from 'actions/customerContacts';
import { fetchIntegrationSettings } from 'actions/integrationSettings';
import CustomerContactsCard from 'components/CustomerContacts/CustomerContactsCard';
import IntegrationSettingsCard from 'components/CustomerImport/EditIntegrationSettings/IntegrationSettingsCard';
import LinkLookup from 'components/LinkLookup';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';
import SettingSidebar from 'components/Navbar/SettingsSidebar';
import Permissions from 'components/Permissions';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCohortsLoading, getUpsertCohortLoading } from 'selectors/cohorts';
import { NAV_BAR_HEIGHT_REM } from 'utils/dimensions';
import CohortsList from './CohortsList';
import CreateCohortModal from './CreateCohortModal';

const AccountOverviewPage: React.FC = () => {
  const dispatch = useDispatch();
  const fetchLoading = useSelector(getCohortsLoading);
  const upsertLoading = useSelector(getUpsertCohortLoading);

  const [isCreatingNew, setIsCreatingNew] = useState(false);

  useEffect(() => {
    dispatch(fetchCustomerContacts());
    dispatch(fetchIntegrationSettings());
  }, [dispatch]);

  const toggleIsCreatingNew = (): void => {
    setIsCreatingNew(!isCreatingNew);
  };

  const warningBarOffset =
    document != null &&
    document.getElementById != null &&
    document.getElementById('customer-warning')?.offsetHeight;

  return (
    <div
      className="min-w-screen bg-white"
      style={{
        height: `calc(100vh - ${warningBarOffset}px)`,
      }}
    >
      <Navbar />
      <div
        className="flex flex-row"
        style={{
          height: `calc(100vh - ${warningBarOffset}px - ${NAV_BAR_HEIGHT_REM}rem)`,
        }}
      >
        <SettingSidebar />
        <div className="px-10 pt-6 pb-6 flex flex-col gap-3 h-full text-body overflow-hidden">
          <header className="flex flex-col gap-2">
            <div className="flex flex-row justify-between items-start">
              <div className="flex flex-row gap-1 items-center">
                <LinkLookup
                  routeName="customer"
                  className="text-body hover:underline"
                  data-testid="customer-button"
                >
                  General
                </LinkLookup>
                &gt;
                <span>Account Overview</span>
              </div>
              <div className="flex flex-row gap-2">
                <Permissions action="analytics.list">
                  <LinkLookup
                    routeName="antitrust-assessment"
                    className="button button--secondary w-45 h-8 font-bold justify-center whitespace-no-wrap"
                    data-testid="generate-assessment-button"
                  >
                    <span className="font-bold">Generate Assessment</span>
                  </LinkLookup>
                  <button
                    type="button"
                    className="button button--primary w-28 h-8 font-bold justify-center whitespace-no-wrap"
                    data-testid="new-cohort-button"
                    onClick={toggleIsCreatingNew}
                  >
                    New Cohort
                  </button>
                </Permissions>
              </div>
            </div>

            <div className="flex flex-row justify-between items-center">
              <div className="flex flex-row items-center">
                <h1 className="text-heading-1">Account Overview</h1>
                {(upsertLoading || fetchLoading) && <LoadingIndicator className="ml-2" size="5" />}
              </div>
            </div>
          </header>
          <div className="flex flex-row gap-6">
            <CustomerContactsCard />
            <IntegrationSettingsCard />
          </div>
          <main className="flex flex-col w-full h-full overflow-hidden">
            <CohortsList />
          </main>
        </div>
      </div>
      {isCreatingNew && (
        <CreateCohortModal
          cohort={null}
          isModalOpen={isCreatingNew}
          toggleModalOpen={toggleIsCreatingNew}
        />
      )}
    </div>
  );
};

export default AccountOverviewPage;
